import React, { useCallback } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';

import { IconButton } from 'ui-components/components/button/icon-button';
import { FlexRow, FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { AcceptTerms } from './accept-terms';
import { messages } from './messages';
import style from './style.module.less';
import { TermLink } from './term-link';
import { DataDrivenLogo } from '../../components/data-driven-logo';
import { Segment } from '../../services/analytics-services/segment';
import { SubmitButton } from '../modals/wallet-registration/components/modal-footer/primary-action-button/submit-button';
import { useWalletRegistrationFormContext } from '../modals/wallet-registration/contexts/wallet-registration-form-context';
import { FORM_REQUIREMENTS } from '../utilities/constants';

const DISMISSAL_EVENT = 'NON_DEFAULT_TERMS_NOTICE_DISMISSED';

/**
 * Renders the UI body for non-default terms notice popup
 * Displays carrier logos, terms acceptance checkbox, term links, and activation button
 * @param {object} props
 * @param {object} props.consolidatedNonDefaultTerms - Object mapping provider IDs to their non-default terms
 * @param {(date: Date) => void} props.dismissNotice - Function to dismiss the notice with tracking
 * @returns {React.JSX.Element} Complete terms notice UI with dismiss functionality
 */
export const NonDefaultTermsNoticeBody = ({ consolidatedNonDefaultTerms, dismissNotice }) => {
  const { walletForm, updateWalletForm } = useWalletRegistrationFormContext();
  const handleNonDefaultTermsChecked = useCallback(
    (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.NONDEFAULT_TERMS], checked);
    },
    [updateWalletForm],
  );

  // Track dismissal, wallet has it's own tracking for activation
  const dismissNoticeWithTracking = useCallback(() => {
    const dismissedAt = new Date();
    dismissNotice(dismissedAt);
    Segment.trackEvent(DISMISSAL_EVENT);
  }, [dismissNotice]);

  return (
    <div className={style.popup}>
      <FlexRow className={style['title-container']}>
        <Text className={style.title} textType={TextType.H3} value={messages.title} />
        <IconButton
          ariaLabelMessageDescriptor={messages.default}
          className={style['title-button']}
          icon={faXmark}
          onClick={dismissNoticeWithTracking}
        />
      </FlexRow>
      <FlexRow className={style['label-provider-logo']}>
        {Object.keys(consolidatedNonDefaultTerms).map((labelProviderId) => {
          return <DataDrivenLogo key={labelProviderId} logoType='icon' providerId={Number.parseInt(labelProviderId, 10)} />;
        })}
      </FlexRow>
      <FlexRow>
        <Text textType={TextType.Paragraph} value={messages.content} />
      </FlexRow>
      <FlexColumn className={style['terms-container']}>
        <AcceptTerms
          checkboxLabel={messages.checkboxTitle}
          disabled={false}
          handleTermsChecked={handleNonDefaultTermsChecked}
          isChecked={walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS]}
        />
        {Object.entries(consolidatedNonDefaultTerms).map(([labelProviderId, nonDefaultTermList]) => {
          return (
            <React.Fragment key={labelProviderId}>
              {nonDefaultTermList.map((term) => {
                const termText = term.termLink?.title ?? messages.default;
                const { version } = term;
                return (
                  <FlexRow key={term.termType}>
                    <TermLink
                      key={term.termType}
                      iconClass={style['terms-link-icon']}
                      linkClass={style['term-link']}
                      termKey={term.termType}
                      termText={termText}
                      termUrl={term?.termLink?.url}
                      version={version}
                    />
                  </FlexRow>
                );
              })}
            </React.Fragment>
          );
        })}
      </FlexColumn>
      <div className={style['button-container']}>
        <SubmitButton
          buttonClassName={style['activate-button']}
          buttonMessageDescriptor={messages.activate}
          disabled={!walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS]}
          isUpdate
        />
      </div>
    </div>
  );
};
